import { DeepReadonly, IOption } from 'types';
import { deepFreeze } from '../utils/deepFreeze.js';

export interface IVisas {
  label: string;
  value: string;
}

export enum CompletedCourses {
  HIGH_SCHOOL = 'High School',
  A_LEVEL = 'A Level',
  BACCALAUREATE = 'Baccalaureate',
  FOUNDATION = 'Foundation',
  COHE = 'CoHE',
  DIPHE = 'DipHE',
  HND = 'HND',
  UG = 'UG',
  PG = 'PG',
  GC = 'GC',
  PGC = 'PGC',
  RESEARCH = 'Research',
}

export const successfullyCompletedCourses: DeepReadonly<IOption[]> = deepFreeze([
  { label: 'High School', value: CompletedCourses.HIGH_SCHOOL },
  { label: 'A Level', value: CompletedCourses.A_LEVEL },
  {
    label: 'International Baccalaureate diploma',
    value: CompletedCourses.BACCALAUREATE,
  },
  { label: 'University Foundation', value: CompletedCourses.FOUNDATION },
  { label: 'Certificate of Higher Education', value: CompletedCourses.COHE },
  {
    label: 'Diploma of Higher Education (DipHE)',
    value: CompletedCourses.DIPHE,
  },
  { label: 'Higher National Diploma (HND)', value: CompletedCourses.HND },
  { label: 'Bachelor Degree (UG)', value: CompletedCourses.UG },
  { label: `Master's Degree (PG)`, value: CompletedCourses.PG },
  { label: 'Graduate Certificate or Diploma', value: CompletedCourses.GC },
  {
    label: 'Post Graduate Certificate or Diploma',
    value: CompletedCourses.PGC,
  },
  { label: 'Research Degree', value: CompletedCourses.RESEARCH },
]);

/**
 * @note - returns value is no match is found, does not throw.
 */
export const getSuccessfullyCompletedCourseLabelFromValue = (value: string): string => {
  const course = successfullyCompletedCourses.find(currentCourse => currentCourse.value === value);
  return course ? course.label : value;
};

/**
 * @note - returns value is no match is found, does not throw.
 */
export const getSuccessfullyCompletedCourseValueFromLabel = (label: string): string => {
  const course = successfullyCompletedCourses.find(currentCourse => currentCourse.label === label);
  return course ? course.value : label;
};

export enum PreviouslyCompletedStudyLevel {
  HIGH_SCHOOL = 'High School',
  FOUNDATION = 'Foundation',
  BACHELOR = 'Bachelor (UG)',
  MASTERS = 'Masters (PG)',
  RESEARCH = 'Research (PhD etc.)',
  SHORT_COURSE = 'Short Course',
  ENGLISH_LANGUAGE_COURSE = 'English Language Course',
}

export const previouslyCompletedStudyLevels: DeepReadonly<IOption[]> = deepFreeze([
  { label: 'High School', value: PreviouslyCompletedStudyLevel.HIGH_SCHOOL },
  { label: 'Foundation', value: PreviouslyCompletedStudyLevel.FOUNDATION },
  { label: 'Bachelor (UG)', value: PreviouslyCompletedStudyLevel.BACHELOR },
  { label: 'Masters (PG)', value: PreviouslyCompletedStudyLevel.MASTERS },
  { label: 'Research (PhD etc.)', value: PreviouslyCompletedStudyLevel.RESEARCH },
  { label: 'Short Course', value: PreviouslyCompletedStudyLevel.SHORT_COURSE },
  {
    label: 'English Language Course',
    value: PreviouslyCompletedStudyLevel.ENGLISH_LANGUAGE_COURSE,
  },
]);

/**
 * @note - returns value is no match is found, does not throw.
 */
export function getPreviouslyCompletedStudyLevelLabelFromValue(value: string): string {
  const level = previouslyCompletedStudyLevels.find(currentLevel => currentLevel.value === value);
  return level ? level.label : value;
}

/**
 * @note - returns label is no match is found, does not throw.
 */
export function getPreviouslyCompletedStudyLevelValueFromLabel(label: string): string {
  const level = previouslyCompletedStudyLevels.find(currentLevel => currentLevel.label === label);
  return level ? level.value : label;
}

/**
 * @note - Summary visa types for previous study question, used on page 1 of history section.
 */
export enum PreviousStudyVisaType {
  SHORT_TERM_STUDY = 'Short-term study / student visa',
  STUDENT_VISA_TIER_4_GENERAL = 'Student visa (Tier 4 General/Student Route)',
  STUDENT_VISA_TIER_4_CHILD = 'Child Student visa (Tier 4 Child/Child Student Route)',
  OTHER = 'Other (my visa is not listed here)',
}

/**
 * @note - Summary visa types for previous study question, used on page 1 of history section.
 */
export const PreviousStudyVisas = [
  { label: 'Short-term study / student visa', value: PreviousStudyVisaType.SHORT_TERM_STUDY },
  {
    label: 'Student visa (Tier 4 General/Student Route)',
    value: PreviousStudyVisaType.STUDENT_VISA_TIER_4_GENERAL,
  },
  {
    label: 'Child Student visa (Tier 4 Child/Child Student Route)',
    value: PreviousStudyVisaType.STUDENT_VISA_TIER_4_CHILD,
  },
  {
    label: 'Other (my visa is not listed here)',
    value: PreviousStudyVisaType.OTHER,
  },
];

/**
 * @note - returns value is no match is found, does not throw.
 */
export const getPreviousStudyVisaLabelFromValue = (value: string): string => {
  const visa = PreviousStudyVisas.find(currentVisa => currentVisa.value === value);
  return visa ? visa.label : value;
};

/**
 * @note - returns label is no match is found, does not throw.
 */
export const getPreviousStudyVisaValueFromLabel = (label: string): string => {
  const visa = PreviousStudyVisas.find(currentVisa => currentVisa.label === label);
  return visa ? visa.value : label;
};

export enum VisaType {
  TOURIST_VISITOR_VISA = 'Tourist / visitor visa',
  INNOVATOR = 'Innovator',
  START_UP = 'Start-up',
  INVESTOR_ENTREPRENEUR_VISA_TIER_1 = 'Investor / entrepreneur visa (Tier 1)',
  EXCEPTIONAL_TALENT_VISA_TIER_1 = 'Exceptional talent visa (Tier 1)',
  GRADUATE_ENTREPRENEUR = 'Graduate Entrepreneur',
  LONG_TERM_WORK_VISA_TIER_2 = 'Long-term work visa (Tier 2)',
  SHORT_TERM_WORK_VISA_TIER_5 = 'Short-term work visa (Tier 5)',
  INTRA_COMPANY_TRANSFER = 'Intra-company Transfer',
  MINISTER_OF_RELIGION = 'Minister of Religion',
  SPORTSPERSON = 'Sportsperson',
  LOW_SKILLED_WORKER = 'Low Skilled Worker',
  STANDARD_VISITOR = 'Standard Visitor',
  MARRIAGE_VISITOR = 'Marriage Visitor',
  PERMITTED_PAID_ENGAGEMENT = 'Permitted Paid Engagement',
  PARENT_OF_A_TIER_4_CHILD = 'Parent of a Tier 4 child',
  CHINESE_TOUR = 'Chinese Tour',
  PASS_THROUGH = 'Pass Through',
  SHORT_TERM = 'Short Term',
  STUDENT_VISA_TIER_4 = 'Student visa (Tier 4 General/Student Route)',
  CHILD_STUDENT_VISA_TIER_4 = 'Child Student visa (Tier 4 Child/Child Student Route)',
  TW_CHARITY = 'TW - Charity',
  TW_CREATIVE_AND_SPORTING = 'TW - Creative & Sporting',
  TW_GOV_AUTH_EXCHANGE = 'TW - Gov Auth Exchange',
  TW_INT_AGREEMENT = 'TW - Int. Agreement',
  TW_RELIGIOUS_WORKER = 'TW - Religious Worker',
  TW_SEASONAL_WORKER = 'TW - Seasonal Worker',
  YOUTH_MOBILITY_SCHEME_VISA_TIER_5 = 'Youth mobility scheme visa (Tier 5)',
  FAMILY_VISAS = 'Family Visas',
  EU_EEA_FAMILY = 'EU/EEA Family',
  DEPENDANT = 'Dependant',
  ADR = 'ADR',
  ACRS = 'ACRS',
  ARAP = 'ARAP',
  ASYLUM = 'Asylum',
  BN_O = 'BN(O)',
  CHARITY_WORKER = 'Charity Worker',
  CHILD_DEPENDANT = 'Child Dependant',
  CHILD_STUDENT = 'Child Student',
  CIVIL_PARTNER = 'Civil Partner',
  CREATIVE_WORKER = 'Creative Worker',
  DIRECT_AIRSIDE_TRANSIT = 'Direct Airside Transit',
  FAMILY_DEPENDANT = 'Family Dependant',
  FIANCE = 'Fiance',
  FRONTIER_WORKER = 'Frontier Worker',
  GLOBAL_TALENT = 'Global Talent',
  GLOBAL_TALENT_DEPENDANT = 'Global Talent Dependant',
  GAE = 'GAE',
  GRADUATE = 'Graduate',
  GRADUATE_DEPENDANT = 'Graduate Dependant',
  GRADUATE_TRAINEE = 'Graduate Trainee',
  HEALTH_AND_CARE_WORKER = 'Health and Care Worker',
  HPI = 'HPI',
  HUMANITARIAN_PROTECTION = 'Humanitarian Protection',
  ILR = 'ILR',
  INDIA_PROFESSIONAL = 'India Professional',
  INDIA_YPS = 'India YPS',
  INNOVATOR_FOUNDER = 'Innovator Founder',
  INNOVATOR_FOUNDER_DEPENDANT = 'Innovator Founder Dependant',
  INTERNATIONAL_AGREEMENT = 'International Agreement',
  INTERNATIONAL_SPORTSPERSON = 'International Sportsperson',
  MINISTER_OF_RELIGION_T2 = 'Minister of Religion (T2)',
  ODW = 'ODW',
  PARENT_OF_A_CHILD = 'Parent of a Child',
  PARENT_OF_A_CHILD_STUDENT = 'Parent of a Child Student',
  PPE = 'PPE',
  REFUGEE_FAMILY_REUNION = 'Refugee Family Reunion',
  RELIGIOUS_WORKER = 'Religious Worker',
  OVERSEAS_BUSINESS_REP = 'Overseas Business Rep',
  RETURNING_RESIDENT = 'Returning Resident',
  SCALE_UP_WORKER = 'Scale-up Worker',
  SEASONAL_WORKER = 'Seasonal Worker',
  SECONDMENT_WORKER = 'Secondment Worker',
  SENIOR_SPECIALIST_WORKER = 'Senior/Specialist Worker',
  SERVICE_PROVIDERS_SWITZERLAND = 'Service Providers (Switzerland)',
  SERVICE_SUPPLIER = 'Service Supplier',
  SKILLED_WORKER = 'Skilled Worker',
  SKILLED_WORKER_DEPENDANT = 'Skilled Worker Dependant',
  SPOUSE = 'Spouse',
  STATELESS_PERSON = 'Stateless Person',
  STUDENT_DEPENDANT = 'Student Dependant',
  TRANSIT_AIRSIDE = 'Transit (Airside)',
  TRANSIT_LANDSIDE = 'Transit (Landside)',
  UK_ANCESTRY = 'UK Ancestry',
  UK_EXPANSION_WORKER = 'UK Expansion Worker',
  UKRAINE_EXTENSION = 'Ukraine Extension',
  UKRAINE_FAMILY = 'Ukraine Family',
  UKRAINE_SPONSORSHIP = 'Ukraine Sponsorship',
  UNMARRIED_PARTNER = 'Unmarried Partner',
  YOUTH_MOBILITY_SCHEME = 'Youth Mobility Scheme',
  OTHER = 'Other',
}

/**
 * @note - returns value is no match is found, does not throw.
 */
export const getVisaLabelFromValue = (value: string): string => {
  const visa = visas.find(currentVisa => currentVisa.value === value);
  return visa ? visa.label : value;
};

/**
 * @note - returns label is no match is found, does not throw.
 */
export const getVisaValueFromLabel = (label: string): string => {
  const visa = visas.find(currentVisa => currentVisa.label === label);
  return visa ? visa.value : label;
};

export const visas: DeepReadonly<IVisas[]> = deepFreeze(
  [
    { label: 'Tourist/visitor', value: VisaType.TOURIST_VISITOR_VISA },
    { label: 'Innovator', value: VisaType.INNOVATOR },
    { label: 'Start-up (legacy/switching only)', value: VisaType.START_UP },
    { label: 'Investor Entrepreneur (Tier 1)', value: VisaType.INVESTOR_ENTREPRENEUR_VISA_TIER_1 },
    { label: 'Exceptional Talent (Tier 1)', value: VisaType.EXCEPTIONAL_TALENT_VISA_TIER_1 },
    { label: 'Graduate Entrepreneur (Tier 1)', value: VisaType.GRADUATE_ENTREPRENEUR },
    { label: 'Long-term Work (Tier 2)', value: VisaType.LONG_TERM_WORK_VISA_TIER_2 },
    { label: 'Short-term Work (Tier 5)', value: VisaType.SHORT_TERM_WORK_VISA_TIER_5 },
    { label: 'Intra-company Transfer (Tier 2)', value: VisaType.INTRA_COMPANY_TRANSFER },
    { label: 'Minister of Religion (Tier 2)', value: VisaType.MINISTER_OF_RELIGION },
    { label: 'Sportsperson (Tier 2)', value: VisaType.SPORTSPERSON },
    { label: 'Low Skilled Worker (Tier 3)', value: VisaType.LOW_SKILLED_WORKER },
    { label: 'Standard Visitor', value: VisaType.STANDARD_VISITOR },
    { label: 'Marriage Visitor', value: VisaType.MARRIAGE_VISITOR },
    { label: 'Permitted Paid Engagement Visitor', value: VisaType.PERMITTED_PAID_ENGAGEMENT },
    { label: 'Parent of a Tier 4 Child', value: VisaType.PARENT_OF_A_TIER_4_CHILD },
    { label: 'ADS Visitor (visiting as a part of a Chinese tour group)', value: VisaType.CHINESE_TOUR },
    { label: 'Visitor in Transit', value: VisaType.PASS_THROUGH },
    { label: 'Short-term Study', value: VisaType.SHORT_TERM },
    { label: 'Student (Tier 4 General/Student Route)', value: VisaType.STUDENT_VISA_TIER_4 },
    {
      label: 'Child Student (Tier 4 Child/Child Student Route)',
      value: VisaType.CHILD_STUDENT_VISA_TIER_4,
    },
    { label: 'Temporary Worker - Charity Worker (Tier 5)', value: VisaType.TW_CHARITY },
    { label: 'Temporary Worker - Creative and Sporting (Tier 5)', value: VisaType.TW_CREATIVE_AND_SPORTING },
    { label: 'Temporary Worker - Government Authorised Exchange (Tier 5)', value: VisaType.TW_GOV_AUTH_EXCHANGE },
    { label: 'Temporary Worker - International Agreement (Tier 5)', value: VisaType.TW_INT_AGREEMENT },
    { label: 'Temporary Worker - Religious Worker (Tier 5)', value: VisaType.TW_RELIGIOUS_WORKER },
    { label: 'Temporary Worker - Seasonal Worker (Tier 5)', value: VisaType.TW_SEASONAL_WORKER },
    { label: 'Youth Mobility Scheme (Tier 5)', value: VisaType.YOUTH_MOBILITY_SCHEME_VISA_TIER_5 },
    { label: 'Appendix FM (spouse, child or ADR)', value: VisaType.FAMILY_VISAS },
    { label: 'EU Settlement Scheme', value: VisaType.EU_EEA_FAMILY },
    { label: 'Dependant (PBS)', value: VisaType.DEPENDANT },

    { label: 'Adult Dependent Relative (ADR)', value: VisaType.ADR },
    { label: 'Afghan Citizens Resettlement Scheme (ACRS)', value: VisaType.ACRS },
    { label: 'Afghan Relocation and Assistance Policy (ARAP)', value: VisaType.ARAP },
    { label: 'Asylum', value: VisaType.ASYLUM },
    { label: 'British National (Overseas) (BN(O))', value: VisaType.BN_O },
    { label: 'Charity Worker (Temporary Work)', value: VisaType.CHARITY_WORKER },
    { label: 'Child Dependant', value: VisaType.CHILD_DEPENDANT },
    { label: 'Child Student', value: VisaType.CHILD_STUDENT },
    { label: 'Civil Partner', value: VisaType.CIVIL_PARTNER },
    { label: 'Creative Worker (Temporary Work)', value: VisaType.CREATIVE_WORKER },
    { label: 'Direct Airside Transit', value: VisaType.DIRECT_AIRSIDE_TRANSIT },
    { label: 'Family Dependant Routes', value: VisaType.FAMILY_DEPENDANT },
    { label: 'Fiancé(e)', value: VisaType.FIANCE },
    { label: 'Frontier Worker', value: VisaType.FRONTIER_WORKER },
    { label: 'Global Talent', value: VisaType.GLOBAL_TALENT },
    { label: 'Global Talent Dependant', value: VisaType.GLOBAL_TALENT_DEPENDANT },
    { label: 'Government Authorised Exchange (Temporary Work)', value: VisaType.GAE },
    { label: 'Graduate', value: VisaType.GRADUATE },
    { label: 'Graduate Dependant', value: VisaType.GRADUATE_DEPENDANT },
    { label: 'Graduate Trainee (Global Business Mobility)', value: VisaType.GRADUATE_TRAINEE },
    { label: 'Health and Care Worker', value: VisaType.HEALTH_AND_CARE_WORKER },
    { label: 'High Potential Individual (HPI)', value: VisaType.HPI },
    { label: 'Humanitarian Protection', value: VisaType.HUMANITARIAN_PROTECTION },
    { label: 'Indefinite Leave to Remain (ILR)', value: VisaType.ILR },
    { label: 'India Professional', value: VisaType.INDIA_PROFESSIONAL },
    { label: 'India Young Professionals Scheme', value: VisaType.INDIA_YPS },
    { label: 'Innovator Founder', value: VisaType.INNOVATOR_FOUNDER },
    { label: 'Innovator Founder Dependant', value: VisaType.INNOVATOR_FOUNDER_DEPENDANT },
    { label: 'International Agreement (Temporary Work)', value: VisaType.INTERNATIONAL_AGREEMENT },
    { label: 'International Sportsperson', value: VisaType.INTERNATIONAL_SPORTSPERSON },
    { label: 'Minister of Religion (T2)', value: VisaType.MINISTER_OF_RELIGION_T2 },
    { label: 'Overseas Domestic Worker (Global Business Mobility)', value: VisaType.ODW },
    { label: 'Parent of a Child', value: VisaType.PARENT_OF_A_CHILD },
    { label: 'Parent of a Child Student', value: VisaType.PARENT_OF_A_CHILD_STUDENT },
    { label: 'Permitted Paid Engagement', value: VisaType.PPE },
    { label: 'Refugee Family Reunion', value: VisaType.REFUGEE_FAMILY_REUNION },
    { label: 'Religious Worker (Temporary Work)', value: VisaType.RELIGIOUS_WORKER },
    { label: 'Representative of an Overseas Business', value: VisaType.OVERSEAS_BUSINESS_REP },
    { label: 'Returning Resident', value: VisaType.RETURNING_RESIDENT },
    { label: 'Scale-up Worker', value: VisaType.SCALE_UP_WORKER },
    { label: 'Seasonal Worker (Temporary Work)', value: VisaType.SEASONAL_WORKER },
    { label: 'Secondment Worker (Global Business Mobility)', value: VisaType.SECONDMENT_WORKER },
    { label: 'Senior or Specialist Worker (Global Business Mobility)', value: VisaType.SENIOR_SPECIALIST_WORKER },
    { label: 'Service Providers from Switzerland', value: VisaType.SERVICE_PROVIDERS_SWITZERLAND },
    { label: 'Service Supplier (Global Business Mobility)', value: VisaType.SERVICE_SUPPLIER },
    { label: 'Skilled Worker', value: VisaType.SKILLED_WORKER },
    { label: 'Skilled Worker Dependant', value: VisaType.SKILLED_WORKER_DEPENDANT },
    { label: 'Spouse', value: VisaType.SPOUSE },
    { label: 'Stateless Person Leave', value: VisaType.STATELESS_PERSON },
    { label: 'Student Dependant', value: VisaType.STUDENT_DEPENDANT },
    { label: 'Transit (Airside)', value: VisaType.TRANSIT_AIRSIDE },
    { label: 'Transit (Landside)', value: VisaType.TRANSIT_LANDSIDE },
    { label: 'UK Ancestry', value: VisaType.UK_ANCESTRY },
    { label: 'UK Expansion Worker (Global Business Mobility)', value: VisaType.UK_EXPANSION_WORKER },
    { label: 'Ukraine Extension Scheme', value: VisaType.UKRAINE_EXTENSION },
    { label: 'Ukraine Family Scheme', value: VisaType.UKRAINE_FAMILY },
    { label: 'Ukraine Sponsorship Scheme', value: VisaType.UKRAINE_SPONSORSHIP },
    { label: 'Unmarried Partner', value: VisaType.UNMARRIED_PARTNER },
    { label: 'Youth Mobility Scheme', value: VisaType.YOUTH_MOBILITY_SCHEME },
    { label: 'Other (My visa is not listed here)', value: VisaType.OTHER },
  ].sort((a, b) => a.label.localeCompare(b.label)),
);
